import { useState, useEffect } from 'react';

interface PipelineConfig {
  workers?: {
    pool_size?: number;
    max_per_task?: number;
    min_per_task?: number;
  };
  rate_limits?: {
    tasks_per_hour?: number;
    api_calls_per_minute?: number;
  };
  execution?: {
    timeout_per_task_seconds?: number;
    max_parallel?: number;
    execution_mode?: string;
  };
}

const DEFAULT_CONFIG: PipelineConfig = {
  workers: {
    pool_size: 40,
    max_per_task: 20,
    min_per_task: 1,
  },
  rate_limits: {
    tasks_per_hour: 120,
    api_calls_per_minute: 2000,
  },
  execution: {
    timeout_per_task_seconds: 300,
    max_parallel: 80,
    execution_mode: 'parallel',
  },
};

export function usePipelineConfig() {
  const [config, setConfig] = useState<PipelineConfig>(DEFAULT_CONFIG);

  useEffect(() => {
    // Load from localStorage
    const saved = localStorage.getItem('pipeline-config-overrides');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setConfig({ ...DEFAULT_CONFIG, ...parsed });
      } catch {
        // ignore
      }
    }

    // Listen for changes
    const handleStorage = (e: StorageEvent) => {
      if (e.key === 'pipeline-config-overrides' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setConfig({ ...DEFAULT_CONFIG, ...parsed });
        } catch {
          // ignore
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  return {
    config,
    maxWorkers: config.workers?.pool_size ?? 40,
    maxPerTask: config.workers?.max_per_task ?? 20,
    minPerTask: config.workers?.min_per_task ?? 1,
    tasksPerHour: config.rate_limits?.tasks_per_hour ?? 120,
    apiCallsPerMinute: config.rate_limits?.api_calls_per_minute ?? 2000,
    taskTimeout: config.execution?.timeout_per_task_seconds ?? 300,
    maxParallel: config.execution?.max_parallel ?? 80,
    executionMode: config.execution?.execution_mode ?? 'parallel',
  };
}

// For components that need a quick sync read
export function getMaxWorkers(): number {
  try {
    const saved = localStorage.getItem('pipeline-config-overrides');
    if (saved) {
      const parsed = JSON.parse(saved);
      return parsed.workers?.pool_size ?? 40;
    }
  } catch {
    // ignore
  }
  return 40;
}
